import { useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { useDispatch, useSelector } from "../../../store";
import { Box, Grid2 } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import FormTextField from "../../forms/FormTextField";
import FormAutocomplete from "../../forms/FormAutocomplete";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import NotificationSchedule from "../../../types/notificationSchedule";
import FormTimePicker from "../../forms/FormTimePicker";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import Option from "../../../types/option";
import { fetchEmailGroups } from "../../../slices/emailGroup";
import { fetchScheduleTypes } from "../../../slices/schedule";

interface FormProps {
  type: "add" | "edit";
  onSubmit: SubmitHandler<NotificationSchedule>;
}

export type ScheduleFormSubmitHandler = () => void;

const AddEditSchedule = forwardRef<ScheduleFormSubmitHandler, FormProps>(
  (props, ref) => {
    const { onSubmit } = props;
    const dispatch = useDispatch();

    const { selectedSchedule, status, scheduleTypes } = useSelector(
      (store) => store.schedules,
    );
    const { emailGroups } = useSelector((store) => store.emails);

    const [weekdays, setWeekdays] = useState<Option[]>();

    const { handleSubmit, control, watch, reset, setValue } =
      useForm<NotificationSchedule>({
        mode: "onChange",
        defaultValues: selectedSchedule,
      });

    const formValues = watch();

    useImperativeHandle(ref, () => handleSubmit(onSubmit));

    const { loadWithSkeleton } = useSkeletonLoader(status === "loading");

    useEffect(() => {
      dayjs.extend(localeData);

      let weekdayList = dayjs.weekdays().map((x, index) => {
        return { id: index + 1, value: x };
      });

      setWeekdays(weekdayList);
    }, []);

    useEffect(() => {
      if (!!selectedSchedule) {
        reset(selectedSchedule);
      }

      return () => {
        reset();
      };
    }, [reset, setValue, selectedSchedule]);

    useEffect(() => {
      dispatch(fetchEmailGroups());
      dispatch(fetchScheduleTypes());
    }, [dispatch]);

    const handleFrequencyChange = () => {
      setValue("day", undefined);
      setValue("dayOfMonth", undefined);
    };

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    label="Schedule Name"
                    control={control}
                    fieldName="name"
                    required
                  />,
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="emailGroups"
                    options={emailGroups || []}
                    required
                    label="Notification Groups"
                    defaultValue={formValues.emailGroups || null}
                    multiple
                    staticFilter
                  />,
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="scheduleType"
                    options={scheduleTypes || []}
                    required
                    label="Frequency"
                    defaultValue={formValues.scheduleType || null}
                    onChange={handleFrequencyChange}
                    staticFilter
                  />,
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTimePicker
                    control={control}
                    fieldName={"time"}
                    label={"Time"}
                    required
                  />,
                )}
              </Grid2>
              {formValues &&
                formValues.scheduleType &&
                formValues.scheduleType?.id === 2 && (
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    {loadWithSkeleton(
                      <FormAutocomplete
                        control={control}
                        fieldName={"day"}
                        label={"Day of Week"}
                        required
                        options={weekdays || []}
                      />,
                    )}
                  </Grid2>
                )}
              {formValues &&
                formValues.scheduleType &&
                formValues.scheduleType?.id === 3 && (
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    {loadWithSkeleton(
                      <FormTextField
                        control={control}
                        fieldName={"dayOfMonth"}
                        label={"Day of Month"}
                        required
                        type="number"
                        maxValue={31}
                      />,
                    )}
                  </Grid2>
                )}
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  },
);

export default AddEditSchedule;
